<template>
  <div>
    <ListToolbar
      v-model:search="search"
      search-placeholder="Search webhooks..."
      :total="pagination.total"
      :page="pagination.page"
      :limit="pagination.limit"
      :active-filter-count="activeFilterCount"
      v-model:sort="sort"
      :sort-options="sortOptions"
      @reload="fetchWebhooks"
      @page-change="goToPage"
      @clear-filters="clearFilters"
    >
      <template #filters>
        <div>
          <label class="block text-sm font-medium text-gray-500 mb-1">Status</label>
          <select v-model="filters.status">
            <option value="">All</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
      </template>
      <template #actions>
        <button v-if="$checkPermission('webhooks.create')" @click="openCreate" class="btn-primary">
          <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          New Webhook
        </button>
      </template>
    </ListToolbar>

    <!-- Table -->
    <div v-if="loading || items.length" :class="{ 'animate-fade-in': !loading }" class="bg-white rounded-xl border border-gray-200/60 table-scroll card-shadow">
      <table class="w-full">
        <thead>
          <tr class="bg-gray-50 border-b border-gray-100">
            <th class="text-left text-sm font-semibold text-gray-500 uppercase tracking-wider px-6 py-3.5">Name</th>
            <th class="text-left text-sm font-semibold text-gray-500 uppercase tracking-wider px-6 py-3.5 hidden md:table-cell">URL</th>
            <th class="text-left text-sm font-semibold text-gray-500 uppercase tracking-wider px-6 py-3.5 hidden lg:table-cell">Events</th>
            <th class="text-left text-sm font-semibold text-gray-500 uppercase tracking-wider px-6 py-3.5">Status</th>
            <th class="text-right text-sm font-semibold text-gray-500 uppercase tracking-wider px-6 py-3.5">Actions</th>
          </tr>
        </thead>
        <tbody v-if="loading">
          <tr v-for="i in 6" :key="'skel-' + i" class="border-b border-gray-50">
            <td class="px-6 py-4">
              <div class="space-y-2"><div class="h-3.5 bg-gray-100 rounded skeleton-pulse" :style="{ width: (40 + i * 5) + '%' }" /></div>
            </td>
            <td class="px-6 py-4 hidden md:table-cell"><div class="h-3.5 bg-gray-100 rounded skeleton-pulse w-20" /></td>
            <td class="px-6 py-4 hidden lg:table-cell"><div class="h-6 bg-gray-100 rounded-md skeleton-pulse w-10" /></td>
            <td class="px-6 py-4"><div class="h-6 bg-gray-100 rounded-full skeleton-pulse w-16" /></td>
            <td class="px-6 py-4"><div class="h-8 bg-gray-100 rounded-lg skeleton-pulse w-20 ml-auto" /></td>
          </tr>
        </tbody>
        <tbody v-else class="divide-y divide-gray-50">
          <tr v-for="item in items" :key="item._id" class="hover:bg-gray-50/50 transition-colors">
            <td class="px-6 py-4">
              <p class="text-[14px] text-gray-900">{{ item.name }}</p>
            </td>
            <td class="px-6 py-4 hidden md:table-cell">
              <span class="text-sm font-data text-gray-500 truncate block max-w-[250px]">{{ item.url }}</span>
            </td>
            <td class="px-6 py-4 hidden lg:table-cell">
              <div class="flex flex-wrap gap-1">
                <span v-for="ev in (item.events || [])" :key="ev" class="inline-flex items-center px-2 py-0.5 rounded text-sm font-medium bg-[var(--color-primary-light)] text-[var(--color-primary)]">{{ eventName(ev) }}</span>
                <span v-if="!item.events?.length" class="text-sm text-gray-300">—</span>
              </div>
            </td>
            <td class="px-6 py-4">
              <span :class="item.status === 'active' ? 'badge-live' : 'badge-idle'">
                {{ item.status || 'inactive' }}
              </span>
            </td>
            <td class="px-6 py-4 text-right">
              <div class="flex items-center justify-end gap-1">
                <ActionsMenu :items="webhookActions(item)" />
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Empty: no matches -->
    <div v-else-if="!loading && (search || filters.status)" class="bg-white rounded-xl border border-gray-200/60 px-6 py-20 text-center card-shadow">
      <div class="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
        <svg class="w-6 h-6 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
      </div>
      <p class="text-base font-medium text-gray-900">No matching webhooks found</p>
      <p class="text-sm text-gray-500 mt-1">Try adjusting your search or filters</p>
    </div>

    <!-- Empty: no records -->
    <div v-else-if="!loading" class="bg-white rounded-xl border border-gray-200/60 px-6 py-20 text-center card-shadow">
      <div class="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
        <svg class="w-6 h-6 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/></svg>
      </div>
      <p class="text-base font-medium text-gray-900">No webhooks yet</p>
      <p class="text-sm text-gray-500 mt-1">Create webhooks to receive event notifications</p>
    </div>

    <!-- Create/Edit Modal -->
    <Teleport to="body">
      <Transition name="modal">
        <div v-if="showModal" class="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div class="absolute inset-0 bg-black/30 backdrop-blur-sm" />
          <div class="relative bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col animate-fade-in">
            <div class="flex items-center justify-between px-6 pt-6 shrink-0">
              <h3 class="text-lg font-semibold text-gray-900">{{ editItem ? 'Edit Webhook' : 'New Webhook' }}</h3>
              <button @click="showModal = false" class="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100">
                <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <form @submit.prevent="saveWebhook" class="flex-1 flex flex-col overflow-hidden">
              <div class="flex-1 overflow-y-auto p-6 pt-5 space-y-4">
              <div>
                <label class="block text-sm font-medium text-gray-600 mb-1.5">Name</label>
                <input v-model="form.name" type="text" required placeholder="Webhook name" class="w-full h-[42px] px-3.5 rounded-lg border border-gray-200 text-sm text-gray-900 placeholder-gray-300 outline-none focus:border-[var(--color-primary)] focus:ring-2 focus:ring-[var(--color-primary-glow)]" />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-600 mb-1.5">URL</label>
                <input v-model="form.url" type="url" required placeholder="https://..." class="w-full h-[42px] px-3.5 rounded-lg border border-gray-200 text-sm text-gray-900 placeholder-gray-300 outline-none focus:border-[var(--color-primary)] focus:ring-2 focus:ring-[var(--color-primary-glow)]" />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-600 mb-1.5">Token</label>
                <input v-model="form.token" type="text" placeholder="Optional secret token" class="w-full h-[42px] px-3.5 rounded-lg border border-gray-200 text-sm text-gray-900 placeholder-gray-300 outline-none focus:border-[var(--color-primary)] focus:ring-2 focus:ring-[var(--color-primary-glow)]" />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-600 mb-2">Events</label>
                <div class="space-y-4">
                  <div v-for="group in eventGroups" :key="group.label" class="rounded-xl bg-gray-50 border border-gray-100 overflow-hidden">
                    <div class="px-4 py-2.5 border-b border-gray-100">
                      <p class="text-xs font-semibold text-gray-500 uppercase tracking-wider">{{ group.label }}</p>
                    </div>
                    <div class="divide-y divide-gray-100">
                      <div v-for="opt in group.events" :key="opt.code" class="flex items-center justify-between px-4 py-3">
                        <div>
                          <p class="text-sm font-medium text-gray-700">{{ opt.name }}</p>
                          <p class="text-sm text-gray-400">{{ opt.description }}</p>
                        </div>
                        <ToggleSwitch :model-value="form.events.includes(opt.code)" @update:model-value="toggleEvent(opt.code, $event)" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              </div>
              <div class="px-6 py-4 border-t border-gray-100 flex justify-end gap-3 shrink-0">
                <button type="button" @click="showModal = false" class="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">Cancel</button>
                <button type="submit" :disabled="saving" class="px-5 py-2 text-sm font-medium bg-[var(--color-primary)] text-white rounded-lg hover:bg-[var(--color-primary-hover)] transition-colors disabled:opacity-50">{{ saving ? 'Saving...' : 'Save' }}</button>
              </div>
            </form>
          </div>
        </div>
      </Transition>
    </Teleport>

    <ConfirmModal :show="showDeleteModal" :loading="deleting" title="Delete Webhook" :message="`Delete '${deleteItem?.name}'? This cannot be undone.`" confirm-text="Delete" :danger="true" @confirm="deleteWebhook" @cancel="showDeleteModal = false" />

    <ConfirmModal
      :show="showToggleModal"
      :loading="toggling"
      :title="toggleItem?.status === 'active' ? 'Deactivate Webhook' : 'Activate Webhook'"
      :message="toggleItem?.status === 'active' ? `Deactivate '${toggleItem?.name}'? It will stop receiving events.` : `Activate '${toggleItem?.name}'? It will start receiving events.`"
      :confirm-text="toggleItem?.status === 'active' ? 'Deactivate' : 'Activate'"
      :danger="toggleItem?.status === 'active'"
      @confirm="doToggleStatus"
      @cancel="showToggleModal = false"
    />

  </div>
</template>

<script>
import ListToolbar from '../../components/ListToolbar.vue'
import ActionsMenu from '../../components/ActionsMenu.vue'
import ConfirmModal from '../../components/ConfirmModal.vue'
import ToggleSwitch from '../../components/ToggleSwitch.vue'
import icons from '../../components/action-icons.js'
import api from '../../services/api.js'
import { notifySuccess } from '../../services/notifications.js'

export default {
  name: 'WebhooksList',
  components: { ListToolbar, ActionsMenu, ConfirmModal, ToggleSwitch },
  data() {
    return {
      loading: true, items: [], search: '', filters: { status: '' }, sort: 'created:-1',
      sortOptions: [
        { value: 'name:1', label: 'Name A-Z' }, { value: 'name:-1', label: 'Name Z-A' },
        { value: 'updated:1', label: 'Last Updated (oldest)' }, { value: 'updated:-1', label: 'Last Updated (newest)' },
        { value: 'created:1', label: 'Created (oldest)' }, { value: 'created:-1', label: 'Created (newest)' }
      ],
      pagination: { limit: 10, page: 1, total: 0 },
      showModal: false, showDeleteModal: false, deleting: false, saving: false, showToggleModal: false, toggling: false, toggleItem: null,
      editItem: null, deleteItem: null,
      form: { name: '', url: '', token: '', events: [] },
      eventGroups: [
        {
          label: 'Schedule',
          events: [
            { name: 'Schedule Started', code: 'schedule.started', description: 'Triggered when a scheduled livestream starts' },
            { name: 'Schedule Ended', code: 'schedule.ended', description: 'Triggered when a scheduled livestream ends' }
          ]
        },
      ],
    }
  },
  computed: {
    activeFilterCount() { return Object.values(this.filters).filter(v => v !== '').length },
    allEventOptions() { return this.eventGroups.flatMap(g => g.events) }
  },
  watch: {
    search() { this.pagination.page = 1; this.fetchWebhooks() },
    sort() { this.pagination.page = 1; this.fetchWebhooks() },
    filters: { deep: true, handler() { this.pagination.page = 1; this.fetchWebhooks() } }
  },
  mounted() { this.fetchWebhooks() },
  methods: {
    clearFilters() { this.filters = { status: '' } },
    async fetchWebhooks(count = true) {
      try {
        const params = { limit: this.pagination.limit, page: this.pagination.page }
        if (this.search) params.text = this.search
        if (this.sort) params.sort = this.sort
        if (this.filters.status) params.status = this.filters.status
        if (count) params.count = true
        const res = await api.get('/webhooks', { params })
        this.items = res.data?.items || []
        if (Number.isInteger(res.data?.total)) this.pagination.total = res.data.total
      } catch { /* keep */ } finally { this.loading = false }
    },
    goToPage(page) { this.pagination.page = page; this.fetchWebhooks(false) },
    openCreate() { this.editItem = null; this.form = { name: '', url: '', token: '', events: [] }; this.showModal = true },
    openEdit(item) {
      this.editItem = item
      this.form = { name: item.name, url: item.url, token: item.token || '', events: [...(item.events || [])] }
      this.showModal = true
    },
    async saveWebhook() {
      this.saving = true
      try {
        if (this.editItem) {
          await api.put(`/webhooks/${this.editItem._id}`, { webhook: this.form })
          notifySuccess('Webhook updated')
        } else {
          await api.post('/webhooks', { webhook: this.form })
          notifySuccess('Webhook created')
        }
        this.showModal = false; this.fetchWebhooks()
      } catch { /* toast */ }
      this.saving = false
    },
    toggleEvent(code, val) {
      if (val) {
        if (!this.form.events.includes(code)) this.form.events.push(code)
      } else {
        this.form.events = this.form.events.filter(e => e !== code)
      }
    },
    eventName(code) {
      const opt = this.allEventOptions.find(o => o.code === code)
      return opt ? opt.name : code
    },
    webhookActions(item) {
      const canUpdate = this.$checkPermission('webhooks.update')
      const active = item.status === 'active'
      return [
        { label: 'Edit', show: canUpdate, action: () => this.openEdit(item), icon: icons.edit },
        { label: 'History', to: { name: 'webhooks-history', params: { id: item._id } }, icon: icons.history },
        { divider: true },
        { label: active ? 'Deactivate' : 'Activate', show: canUpdate, action: () => this.toggleStatus(item), icon: active ? icons.pause : icons.play },
        { divider: true },
        { label: 'Delete', show: this.$checkPermission('webhooks.delete'), danger: true, action: () => this.confirmDelete(item), icon: icons.delete }
      ]
    },
    toggleStatus(item) {
      this.toggleItem = item
      this.showToggleModal = true
    },
    async doToggleStatus() {
      this.toggling = true
      try {
        const action = this.toggleItem.status === 'active' ? 'deactivate' : 'activate'
        await api.put(`/webhooks/${this.toggleItem._id}/status`, { action })
        notifySuccess(`Webhook ${action}d`)
        this.fetchWebhooks()
      } catch { /* toast */ } finally {
        this.toggling = false
        this.showToggleModal = false
        this.toggleItem = null
      }
    },
    confirmDelete(item) { this.deleteItem = item; this.showDeleteModal = true },
    async deleteWebhook() {
      this.deleting = true
      try { await api.delete(`/webhooks/${this.deleteItem._id}`); notifySuccess('Webhook deleted'); this.fetchWebhooks() } catch { /* toast */ }
      this.showDeleteModal = false; this.deleteItem = null
      this.deleting = false
    }
  }
}
</script>
<style scoped>
.modal-enter-active, .modal-leave-active { transition: opacity 0.2s ease; }
.modal-enter-from, .modal-leave-to { opacity: 0; }
</style>
